import { useState, useRef } from "react";
import MessageInput from "./subcomponents/message-input/message-input";
import { initiateChatSession } from "~/lib/network/chat";
import { initiateTechnicalSession } from "~/lib/network/technical-session";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/stores/store-provider";
import DataSourceSelectorModal from "~/components/database/data-source-selector-modal";

const getTimeBasedGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const welcomeQuotes = [
  "Welcome! Let's analyze your business data and uncover valuable insights.",
  "Ready to dive into your business metrics? Let's get started!",
  "Data-driven decisions start here. What would you like to analyze today?",
  "Your business intelligence assistant is ready. What can I help you with?",
  "Let's turn your data into actionable strategies. What's on your mind?",
  "Welcome to your personal business analyst. How can I assist you today?",
  "Unlock the potential of your business data. What would you like to explore?",
  "Your virtual business consultant is here. What questions do you have?",
  "Let's leverage data to drive your business forward. Where shall we begin?",
  "Welcome to intelligent business analysis. What insights are you seeking?",
  "Your data has stories to tell. What would you like to discover?",
  "Ready to make informed business decisions? Let's analyze your data.",
];

const getRandomQuote = () => {
  const randomIndex = Math.floor(Math.random() * welcomeQuotes.length);
  return welcomeQuotes[randomIndex];
};

interface ChatHomeProps {
  onOpenDatabaseModal?: () => void;
}

const ChatHome = ({ onOpenDatabaseModal }: ChatHomeProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [randomQuote, setRandomQuote] = useState(getRandomQuote());
  const [showTechnicalDialog, setShowTechnicalDialog] = useState(false);
  const [sessionName, setSessionName] = useState("");
  const [isCreatingTechnical, setIsCreatingTechnical] = useState(false);
  const [showDataSourceModal, setShowDataSourceModal] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string>("");
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  const dataSourceModalRef = useRef<HTMLDialogElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    selectedDatabase,
    setSelectedDatabase,
    setCurrentSession,
    clearChatMessages,
    resetAnalysisDocument,
    setSessionDocumentView,
    connectionsList,
    chatSessions,
  } = useGlobalStore((state: any) => state);

  const [selectedDataSources, setSelectedDataSources] = useState<string[]>(
    () => {
      const shopifyConnection = connectionsList.find(
        (conn: any) => conn.connection_type === "shopify"
      );
      return shopifyConnection ? [shopifyConnection._id] : [];
    }
  );

  const handleSendMessage = async (message: string, attachments?: File[]) => {
    // Check if data sources are selected AND no files attached
    if (
      selectedDataSources.length === 0 &&
      (!attachments || attachments.length === 0)
    ) {
      // No data sources and no files, show modal
      setPendingMessage(message);
      setPendingAttachments(attachments || []);
      setShowDataSourceModal(true);
      dataSourceModalRef.current?.showModal();
      return;
    }

    // Proceed with session creation
    await createSession(message, attachments);
  };

  const createSession = async (message: string, attachments?: File[]) => {
    setIsLoading(true);
    try {
      const response = await initiateChatSession(
        message,
        selectedDataSources,
        attachments
      );
      setCurrentSession(response);
      const { session_id: sessionId } = response;
      navigate(`/session/vibe/${sessionId}`);
    } catch (error) {
      console.error("Failed to create session:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDataSourceSelectionChange = (newSelection: string[]) => {
    setSelectedDataSources(newSelection);
  };

  const handleDataSourceModalClose = async () => {
    setShowDataSourceModal(false);
    dataSourceModalRef.current?.close();

    // If there's a pending message and sources are now selected, create session
    if (pendingMessage && selectedDataSources.length > 0) {
      await createSession(pendingMessage, pendingAttachments);
      setPendingMessage("");
      setPendingAttachments([]);
    } else {
      // Just clear pending message if user closed without selecting
      setPendingMessage("");
      setPendingAttachments([]);
    }
  };

  const handleDatabaseSelect = (database: string) => {
    setSelectedDatabase(database);
  };

  const handleStartTechnicalAnalysis = async () => {
    if (!sessionName.trim()) return;

    // Clear any existing session state before creating new technical session
    clearChatMessages();
    resetAnalysisDocument();
    setSessionDocumentView(false);

    setIsCreatingTechnical(true);
    try {
      const response = await initiateTechnicalSession(sessionName.trim());
      navigate(`/session/technical/${response.session_id}`);
    } catch (error) {
      console.error("Failed to create technical session:", error);
    } finally {
      setIsCreatingTechnical(false);
      setShowTechnicalDialog(false);
      setSessionName("");
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex flex-col items-center justify-start pt-48 gap-4">
        <div className="text-center space-y-6 mb-8">
          <h1 className="text-4xl md:text-5xl font-semibold text-base-content">
            {getTimeBasedGreeting()}!
          </h1>
        </div>

        {/* Migration welcome section */}

        <div className="text-center mb-4">
          <p className="text-lg text-base-content/60 font-medium max-w-2xl">
            "Click on the glowing button and start exploring — use our sample
            databases, connect your database, or upload files."
          </p>
        </div>

        <div className="w-3xl">
          <MessageInput
            onSendMessage={handleSendMessage}
            placeholder="Ask Gamgee"
            attachmentsRequired={false}
            isLoading={isLoading}
            selectedDatabase={selectedDatabase}
            onDatabaseSelect={handleDatabaseSelect}
            isDatabaseSelectionDisabled={false}
            isHome={true}
            selectedDataSources={selectedDataSources}
            onDataSourcesChange={setSelectedDataSources}
          />
        </div>

        {/* Add separator */}
        <div className="flex items-center my-8 px-8 max-w-3xl mx-auto w-full">
          <div className="flex-1 border-t border-base-300"></div>
          <span className="px-4 text-base-content/40 text-sm">OR</span>
          <div className="flex-1 border-t border-base-300"></div>
        </div>

        {/* Technical Analysis button */}
        <div className="text-center pb-8">
          <button
            onClick={() => setShowTechnicalDialog(true)}
            className="btn btn-outline btn-primary btn-lg"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Launch a Notebook
          </button>
          <p className="text-sm text-base-content/60 mt-8">
            Launch a Gamgee Notebook for advanced data analysis
          </p>
        </div>
      </div>

      {/* Technical Session Dialog Modal */}
      {showTechnicalDialog && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-8">
              Create Technical Analysis Session
            </h3>

            <div className="form-control w-full">
              <label className="label mb-4">
                <span className="label-text">Title</span>
              </label>
              <input
                type="text"
                placeholder="E.g., Q4 Marketing Plan"
                className="input input-bordered w-full mb-4"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && sessionName.trim()) {
                    handleStartTechnicalAnalysis();
                  }
                }}
                autoFocus
              />
            </div>

            <div className="modal-action">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setShowTechnicalDialog(false);
                  setSessionName("");
                }}
                disabled={isCreatingTechnical}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleStartTechnicalAnalysis}
                disabled={!sessionName.trim() || isCreatingTechnical}
              >
                {isCreatingTechnical ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Creating...
                  </>
                ) : (
                  "Create Session"
                )}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={() => setShowTechnicalDialog(false)}>close</button>
          </form>
        </dialog>
      )}

      {/* Data Source Selector Modal */}
      <DataSourceSelectorModal
        ref={dataSourceModalRef}
        isOpen={showDataSourceModal}
        onClose={handleDataSourceModalClose}
        initialSelectedSources={selectedDataSources}
        onSelectionChange={handleDataSourceSelectionChange}
      />
    </div>
  );
};

export default ChatHome;
